import { Button as BaseButton } from '@base-ui/react/button'
import { forwardRef } from 'react'
import type { ComponentProps, ReactElement, ReactNode } from 'react'
import { buttonClasses } from './styles/button-classes'
import type { ButtonSize, ButtonVariant } from './styles/button-variant'
import type { ActionTone } from './styles/tones'

/**
 * `tone` y `variant` son dos ejes independientes: las 12 celdas existen todas, ninguna prohibida en
 * el tipo. `ActionTone` (`primary`/`info`/`error`) es el eje del botón — sin `success`/`warning`,
 * que son resultado/severidad de `FeedbackTone` (`Toast`/`Callout`/`Progress`), no algo que un botón
 * hace. El tipo lo declara `styles/tones.ts` (junto a la tabla de clases, compartida con `Chip`) y
 * se re-exporta acá. Rationale completo de los tres ejes de tono de la capa: ADR-0016.
 */
export type { ActionTone }

/**
 * El peso y el tamaño. Los dos unions y sus tablas de clase viven en `styles/button-variant.ts`,
 * que es de donde también los lee el `Toggle` — una constante compartida no puede vivir al lado de
 * un componente sin romperle el fast-refresh. Se re-exportan acá, igual que `ActionTone`.
 */
export type { ButtonSize, ButtonVariant }

type ButtonShared = Omit<ComponentProps<typeof BaseButton>, 'children' | 'aria-label' | 'aria-labelledby'> & {
  /** De qué habla la acción. Default `primary`. */
  tone?: ActionTone
  /** Cuánto pesa la acción. Default `solid`. */
  variant?: ButtonVariant
  size?: ButtonSize
}

/**
 * `icon` es un discriminated union, no un `boolean` suelto: `icon: true` exige `children` de tipo
 * `ReactElement` (rechaza un string o un glifo Unicode suelto, que son `string`, no `ReactElement`)
 * y uno de `aria-label`/`aria-labelledby`. `tsc` rechaza el caso roto antes de que exista — no hace
 * falta un auditor que abra el árbol JSX para encontrarlo.
 *
 * Lo que el tipo **no** puede cubrir: un `children` de más de un nodo (`{' '}<IconClose />`, el
 * espacio de formato que deja Prettier) sigue siendo válido para TS porque no hay forma de tipar
 * "string que es solo espacio" distinto de cualquier otro string sin rechazar ese caso legítimo. Ese
 * borde lo sigue cazando `audit:props` — ver su comentario en `scripts/audit-props.mjs`.
 *
 * `ButtonShared` va distribuido en cada rama en vez de intersecado una sola vez
 * (`ButtonShared & (A | B)`): `forwardRef` deriva el tipo público con `PropsWithoutRef`, que solo
 * distribuye un conditional type cuando el parámetro sustituido ES la union — con la union anidada
 * dentro de una intersección, `Omit`/`Pick` la aplanaban y `<Button icon />` sin
 * `children`/`aria-label` dejaba de marcar error (verificado con un caso roto a propósito). Un
 * conditional type que la redistribuyera automáticamente typechequea, pero `audit:props` no lo
 * resuelve — su walker lee uniones e intersecciones literales, no conditional types
 * (`scripts/audit-props.mjs` en `layerInterfaces`) — así que la distribución va escrita a mano.
 */
export type ButtonProps =
  | (ButtonShared & { icon: true; children: ReactElement } & (
      | { 'aria-label': string; 'aria-labelledby'?: undefined }
      | { 'aria-label'?: undefined; 'aria-labelledby': string }
    ))
  | (ButtonShared & { icon?: false; children?: ReactNode; 'aria-label'?: string; 'aria-labelledby'?: string })

/**
 * Se usa el `Button` de Base UI y no un `<button>` pelado por tres cosas: expone `data-disabled`
 * para estilar sin depender de `:disabled`, se compone con el `render` de los triggers (popover,
 * menú, tooltip) sin anidar un botón dentro de otro, y permite `focusableWhenDisabled`.
 *
 * **La capa activa `focusableWhenDisabled` por default, y es una decisión.** Base UI lo trae en
 * `false`, con lo que un botón deshabilitado emite `disabled` nativo y el navegador lo saca del
 * orden de tabulación —el `tabindex="0"` que igual escribe queda inerte—. Eso tiene dos costos: un
 * usuario de teclado nunca descubre que la acción existe, y un `Tooltip` encima no se abre nunca,
 * porque un botón con `disabled` nativo no recibe eventos de puntero. Es el caso al que la doc de
 * shadcn le dedica una sección entera, resuelto envolviendo el botón en un `span`.
 *
 * Con la prop activada, Base UI usa `aria-disabled` en lugar del atributo nativo: el botón sigue
 * siendo alcanzable y anunciado como deshabilitado, la librería bloquea la activación, y el tooltip
 * funciona sin envolver nada. Es lo que recomienda APG para controles de barra.
 *
 * El precio es que un botón deshabilitado toma foco, que puede sorprender. Se apaga por instancia
 * con `focusableWhenDisabled={false}`.
 *
 * **No es para enlaces.** Lo que navega es un `<a>`, y un `Button` con `render={<Link/>}` lo anuncia
 * como botón: para eso está `ButtonLink`.
 */
// `forwardRef` es un shim de React 18: la capa corre React 19, donde la `ref` ya viaja dentro de
// `props`. Se retira con el codemod `react/19/remove-forward-ref` cuando el último consumidor
// migre (ADR-0028).
export const Button = forwardRef<HTMLElement, ButtonProps>(function Button(
  { tone = 'primary', variant = 'solid', size = 'md', icon = false, focusableWhenDisabled = true, className, ...props },
  ref,
) {
  // Base UI admite `className` como función del estado; se resuelve antes de sumarla a la receta.
  return (
    <BaseButton
      ref={ref}
      className={(state) =>
        buttonClasses(tone, variant, size, icon, typeof className === 'function' ? className(state) : className)
      }
      focusableWhenDisabled={focusableWhenDisabled}
      {...props}
    />
  )
})

Button.displayName = 'Button'
